import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomTextDto, TextLocale } from '@fyr/shared';
import { api } from './api';
import i18n from './i18n';

/**
 * Customisation console — the app's side (kept tiny: it's in the first load).
 *   • Text overrides: GET /customisation/text (the platform's, + the organisation's once signed in) laid over the
 *     bundled strings in i18next's store at runtime — no rebuild. Re-laid whenever a bundle arrives (the rest of the
 *     English, or French), since that can overwrite them. React renders strings as text, so an override can't inject
 *     markup (and the api and database refuse it anyway).
 *   • Edit mode (super admins, click-to-edit on real screens): only a flag in THIS browser, switched on from the
 *     Platform console. The overlay loads only if this browser also holds a live console session — checked with the
 *     api before anything is shown, and every save goes to the console's api, which checks again. Nothing about it is
 *     stored on the server, so it can't reach anyone else's session.
 */
const lngsFor = (locale: string) => (locale === 'en-GB' ? ['en-GB', 'en'] : [locale]);
let wanted: CustomTextDto['text'] = {};
/** What we laid over each language, and what was there before (to put back when an override is removed). */
const applied: Record<string, Record<string, string>> = {};
const before: Record<string, Record<string, string | undefined>> = {};
let applying = false;

function lay() {
  let changed = false;
  applying = true;
  try {
    const locales = new Set([
      ...Object.keys(wanted),
      ...Object.keys(applied).map((l) => (l === 'en' ? 'en-GB' : l)),
    ]);
    for (const locale of locales)
      for (const lng of lngsFor(locale)) {
        if (!i18n.hasResourceBundle(lng, 'translation')) continue; // laid when it arrives
        const want = wanted[locale as TextLocale] ?? {};
        const had = applied[lng] ?? {};
        const was = (before[lng] ??= {});
        for (const key of Object.keys(had))
          if (!(key in want) && was[key] !== undefined) {
            i18n.addResource(lng, 'translation', key, was[key]!, { silent: true });
            changed = true;
          }
        const now: Record<string, string> = {};
        for (const [key, value] of Object.entries(want)) {
          const cur = i18n.getResource(lng, 'translation', key) as string | undefined;
          if (cur !== value) {
            if (had[key] !== cur) was[key] = cur;
            i18n.addResource(lng, 'translation', key, value, { silent: true });
            changed = true;
          }
          now[key] = value;
        }
        applied[lng] = now;
      }
    // re-render (react-i18next listens for 'added')
    if (changed)
      (i18n.store as unknown as { emit: (e: string, ...a: unknown[]) => void }).emit(
        'added',
        i18n.language,
        'translation',
      );
  } finally {
    applying = false;
  }
}
i18n.store.on('added', () => {
  if (!applying) lay();
});

let refresh: () => void = () => undefined;
/** Fetch the text overrides again now (after a save in the console or the editor). */
export const refreshCustomText = () => refresh();

// ── edit mode ──────────────────────────────────────────────────────────────────────────────────────────────────
const EDIT_KEY = 'fyr.customise.edit';
export const EDIT_EVENT = 'fyr-customise-edit';
/** Switch click-to-edit on (until the console session's end) or off, in this browser only. */
export function setTextEditMode(until: string | null) {
  try {
    if (until) localStorage.setItem(EDIT_KEY, JSON.stringify({ until }));
    else localStorage.removeItem(EDIT_KEY);
  } catch {
    /* private mode: edit mode simply isn't available */
  }
  window.dispatchEvent(new Event(EDIT_EVENT));
}
export function textEditModeOn(): boolean {
  try {
    const v = JSON.parse(localStorage.getItem(EDIT_KEY) ?? 'null') as { until?: string } | null;
    if (v?.until && new Date(v.until) > new Date()) return true;
    if (v) localStorage.removeItem(EDIT_KEY);
  } catch {
    /* ignore */
  }
  return false;
}

/** Mounted once in the app root: text overrides for whoever is signed in, and the super admin's edit mode. */
export function useCustomisation(tenant: { id: string; name: string } | undefined) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['custom-text', tenant?.id ?? null],
    queryFn: () => api.get<CustomTextDto>('/customisation/text'),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
  useEffect(() => {
    refresh = () => void qc.invalidateQueries({ queryKey: ['custom-text'] });
  }, [qc]);
  useEffect(() => {
    if (!q.data) return;
    wanted = q.data.text ?? {};
    lay();
  }, [q.data]);

  useEffect(() => {
    let stop: (() => void) | null = null;
    let starting = false;
    let alive = true;
    const sync = () => {
      if (textEditModeOn()) {
        if (stop || starting) return;
        starting = true;
        import('./components/platform/TextEditMode')
          .then((m) => m.startTextEditMode(tenant ?? null))
          .then((s) => {
            if (alive && textEditModeOn() && !stop) stop = s;
            else s();
          })
          .catch(() => undefined)
          .finally(() => (starting = false));
      } else if (stop) {
        stop();
        stop = null;
      }
    };
    sync();
    window.addEventListener(EDIT_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      alive = false;
      window.removeEventListener(EDIT_EVENT, sync);
      window.removeEventListener('storage', sync);
      stop?.();
    };
  }, [tenant?.id, tenant?.name]);
}
